//
// cookie-monster.js
//
// Simple cookie notification controller
//

import {get_cookie, set_cookie, delete_cookie} from './crumblr';
import {add_classes, remove_classes} from '../helpers/classlist';
import cookies from './cookie-acceptance';
import * as value_extractor from '../forms/value-extractor';
import { path_base } from '../bw-paths';


const attr__prefs_section = 'data-cookie-prefs-section';
const cats = ['functional', 'statistical', 'marketing'];
let is_gdpr = false;


function spinner(wrapper) {
  return {
    show_then_hide() {
      setTimeout(_ => {
        wrapper.classList.add('t-active-wrap');
        setTimeout(_ => wrapper.classList.remove('t-active-wrap'), 500);
      }, 300);
    },
  };
}



// controller for the cookie banner
// ----------------------------------------

function controller__cookie_notification_popup(wrapper, is_gdpr = false) {


  // Check if script counts have changed
  const cookie_counts_have_changed = cats.reduce((result, category) => {

    const counts             = window.__optional_scripts__count;
    const current_count      = counts && counts[category] || 0;
    const cookie_name__count = `bw_cookies__strict__${category}__prev_count`;
    const previous_count     = parseInt(get_cookie(cookie_name__count));
    let count_has_changed    = false;

    // Note: this currently checks if there are MORE cookies in a certain category
    //       than there were previously - but can obviously check for any change in count
    //       if desired.

    if (previous_count < current_count || isNaN(previous_count)) {
      count_has_changed = true;
      delete_cookie(`bw_cookies__strict__approved__${category}`,  path_base(), window.location.hostname);
    }

    if (is_gdpr) {
      set_cookie(cookie_name__count, current_count, cookies.DURATION);
    }

    return result || count_has_changed;
  }, false);


  if (!wrapper                                     ||
      cookies.legacy.is_accepted()                 ||
      cookies.default_banner_dismiss.is_accepted() ||
      (is_gdpr && !(cookies.uninitialized() || cookie_counts_have_changed))) {
    return false;
  }


  const btn__accept = wrapper.querySelector('[data-js-cookie-notification-accept]');


  function show() {
    add_classes(wrapper, 't-active-wrap');
    remove_classes(wrapper, 'o-0 pe-none');
  }


  function hide() {
    remove_classes(wrapper, 't-active-wrap');
    add_classes(wrapper, 'pe-none o-0');
  }


  function accept(e) {
    e.preventDefault()
    
    hide();
    if (is_gdpr) {
      cookies.set_all(cookies.ACCEPTED);
      global_inject_optional_scripts();
    }
    else {
      cookies['default_banner_dismiss'].set(cookies.ACCEPTED);
    }
  }


  function init() {
    btn__accept.addEventListener('click', accept);
    show();
  }
  init();

  return {
    show,
    hide,
  };
}


// cookie prefs page
// ----------------------------------------

function controller__cookie_prefs_page() {
  const section_divs = Array.from(document.querySelectorAll(`[${attr__prefs_section}]`));

  if (section_divs.length) {
    const sections = section_divs.length && section_divs.map(controller__cookie_prefs_section);
    controller__all_toggle(document.querySelector('[data-cookie-prefs-overall-setter]'), sections);
    cats.forEach((cat) => {
      if (cookies[cat].is_uninitialized()) {
        cookies[cat].set(cookies.REJECTED);
      }
    });
  }
}


// cookie prefs page - a controller for each section/category of cookie
// ----------------------------------------

function controller__cookie_prefs_section(wrapper) {
  let inputs;
  let spin;
  const section_name = wrapper.getAttribute(attr__prefs_section);


  function cb_set() {
    const value = value_extractor.get_value(inputs);

    spin.show_then_hide();
    setTimeout(() => window.dispatchEvent(new CustomEvent('sitewide-ui-notification/show')), 1000);
    setTimeout(() => window.dispatchEvent(new CustomEvent('sitewide-ui-notification/hide')), 7000);

    const acceptance = {
      'enabled':  cookies.ACCEPTED,
      'disabled': cookies.REJECTED,
    }[value] || null;

    if (acceptance) {
      cookies[section_name].set(acceptance);
      global_inject_optional_scripts();
    }
  }


  function set_value(value) {
    if (inputs.length) {
      value_extractor.set_value(inputs, value);
      cb_set();
    }
  }


  function init() {
    spin = spinner(wrapper.querySelector('.js-cookie-spinner'));

    inputs = wrapper.querySelectorAll('input');
    if (inputs.length === 0) {
      return;   // Not all cookie prefs sections are togglable
    }


    // Bind
    inputs.forEach(input => input.addEventListener('change', cb_set));

    // Set initial state
    if (cookies[section_name].is_accepted()) {
      value_extractor.set_value(inputs, 'enabled');
    }
    else if (cookies[section_name].is_rejected()) {
      value_extractor.set_value(inputs, 'disabled');
    }

  }
  init();


  return {
    set_value,
  };
}



// controller for all-toggler that sets every category at once
// ----------------------------------------

function controller__all_toggle(wrapper, sections) {
  if (!wrapper) {
    return;
  }

  if (!is_gdpr && (window.location.href.indexOf('legal/cookie-preferences') !== -1)) {
    const current_url    = window.location.href;
    const new_url        = current_url.replace('legal/cookie-preferences', 'legal/cookie-policy');
    window.location.href = new_url;
  }

  const btn__enable_all  = wrapper.querySelector('[data-js-cookie-opt-in-btn]');
  const btn__disable_all = wrapper.querySelector('[data-js-cookie-opt-out-btn]');
  const spin = spinner(wrapper.querySelector('.js-cookie-spinner'));

  if (!btn__enable_all || !btn__disable_all) {
    return;
  }

  btn__enable_all.addEventListener('click', ev => {
    ev.preventDefault()
    
    spin.show_then_hide();
    sections.forEach(section => section.set_value('enabled'));
  });

  btn__disable_all.addEventListener('click', ev => {
    ev.preventDefault()
    
    spin.show_then_hide();
    sections.forEach(section => section.set_value('disabled'));
  });

  return { };
}



// init()
// ----------------------------------------

function init() {
  if (typeof global_inject_optional_scripts !== 'function') {
    return false;
  }


  is_gdpr      = global_is_gdpr_country(get_cookie('bw_geo'));
  let selector = is_gdpr ? 'data-js-cookie-notification-strict-mode' : 'data-js-cookie-notification-default-mode';

  // if this selector is present, the system has been set to non-gdpr/implict mode in wp-config
  if (document.querySelector('[data-js-cookie-notification-manual-mode]')) {
    selector = 'data-js-cookie-notification-manual-mode';
    is_gdpr  = false;
  }

  // If NOT gdpr mode and gdpr cookies HAVE been set, remove them.
  if (!is_gdpr) {
    cats.forEach((cat) => {
      delete_cookie(`bw_cookies__strict__approved__${cat}`,    path_base(), window.location.hostname);
      delete_cookie(`bw_cookies__strict__${cat}__prev_count`, path_base(), window.location.hostname);
    });
    set_cookie('bw_cookies__mode', 'default', cookies.DURATION);
  }
  else {
    set_cookie('bw_cookies__mode', 'strict', cookies.DURATION);
  }


  // Inject Scripts
  // -------------------------------------

  if (!is_gdpr || cookies.legacy.is_accepted() || cookies.default_banner_dismiss.is_accepted()) {
     // inject all scripts
    global_inject_optional_scripts(true);
  }
  else {
    // inject scripts for each category that’s been approved
    // - if first visit will be none
    global_inject_optional_scripts();
  }


  // Fire Controllers
  // -------------------------------------

  controller__cookie_notification_popup(document.querySelector(`[${selector}]`), is_gdpr);
  controller__cookie_prefs_page();
}


if (document.getElementById('bw-cookie-sys-active') || document.querySelector('[data-cookie-prefs-overall-setter]')) {
  window.global_geo_ip_promise.then(init).catch(() => console.log('Cookie System: Geo Location promise from IPStack unresolved.'));
}

